import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch, faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons'
import Spinner from '@/components/Spinner'
import { UserListTableProps, SortState } from './types'

/**
 * Reusable UserListTable component for Organizers and Singers
 * Supports search, filtering, sorting, pagination, and row actions
 */
export default function UserListTable({
  users,
  columns,
  loading = false,
  error = null,
  searchPlaceholder = 'Rechercher...',
  filters = [],
  pagination = { page: 1, pageSize: 25, total: users.length },
  onSearch,
  onFilterChange,
  onSort,
  onPaginationChange,
  onRowClick,
  onAction,
  userType = 'organizer',
  emptyMessage,
  getRowClassName,
}: UserListTableProps) {
  const { t } = useTranslation()
  const [searchTerm, setSearchTerm] = useState('')
  const [filterValues, setFilterValues] = useState<Record<string, any>>({})
  const [sortState, setSortState] = useState<SortState>({
    key: null,
    direction: 'asc',
  })

  // Handle search
  const handleSearch = (value: string) => {
    setSearchTerm(value)
    onSearch?.(value)
  }

  // Handle filter change
  const handleFilterChange = (key: string, value: any) => {
    const newFilters = { ...filterValues, [key]: value }
    setFilterValues(newFilters)
    onFilterChange?.(newFilters)
  }

  // Handle sort
  const handleSort = (key: string) => {
    if (!columns.find((col) => col.key === key && col.sortable)) return

    const newDirection =
      sortState.key === key && sortState.direction === 'asc' ? 'desc' : 'asc'
    setSortState({ key, direction: newDirection })
    onSort?.(key)
  }

  // Calculate pagination
  const pageCount = Math.ceil(pagination.total / pagination.pageSize)
  const startPage = Math.max(1, pagination.page - 2)
  const endPage = Math.min(pageCount, pagination.page + 2)
  const paginationRange = Array.from(
    { length: endPage - startPage + 1 },
    (_, i) => startPage + i
  )

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <Spinner />
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-4">
        <p className="text-red-300 font-mono text-sm">
          <span className="text-red-500">{'[ERROR]'}</span> {error}
        </p>
      </div>
    )
  }
// console.log('users', users)
  return (
    <div className="space-y-6">
      {/* Search and Filters Bar */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-4">
        <div className="flex gap-4 flex-wrap items-end">
          {/* Search Input */}
          <div className="flex-1 min-w-64">
            <label className="block text-xs font-mono font-bold text-cyan-400 mb-2 uppercase tracking-wider">
              {t('common.search')}
            </label>
            <div className="relative">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm"
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 font-mono text-sm focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/50 transition"
                placeholder={searchPlaceholder}
              />
            </div>
          </div>

          {/* Filters */}
          {filters.map((filter) => (
            <div key={filter.key}>
              <label className="block text-xs font-mono font-bold text-cyan-400 mb-2 uppercase tracking-wider">
                {filter.label}
              </label>
              {filter.type === 'select' && (
                <select
                  value={filterValues[filter.key] || 'all'}
                  onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                  className="px-4 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 font-mono text-sm focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/50 transition"
                >
                  <option value="all">{t('common.all')}</option>
                  {filter.options?.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              )}
              {filter.type === 'text' && (
                <input
                  type="text"
                  value={filterValues[filter.key] || ''}
                  onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                  placeholder={filter.placeholder}
                  className="px-4 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 font-mono text-sm focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/50 transition"
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        {users.length === 0 ? (
          <div className="p-8 text-center text-slate-400 font-mono">
            {'$ '} {emptyMessage || t('common.noData')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-800/50 border-b border-slate-700/50">
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      onClick={() => column.sortable && handleSort(column.key)}
                      className={`px-6 py-4 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider ${
                        column.sortable ? 'cursor-pointer hover:bg-slate-700/50' : ''
                      }`}
                      style={column.width ? { width: column.width } : undefined}
                    >
                      <div className="flex items-center gap-2">
                        {column.label}
                        {column.sortable && sortState.key === column.key && (
                          <FontAwesomeIcon
                            icon={
                              sortState.direction === 'asc'
                                ? faChevronUp
                                : faChevronDown
                            }
                            className="text-purple-400 text-xs"
                          />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {users.map((user) => (
                  <tr
                    key={user.id}
                    onClick={() => onRowClick?.(user)}
                    className={getRowClassName ? getRowClassName(user) : "hover:bg-slate-800/50 transition group cursor-pointer"}
                  >
                    {columns.map((column) => {
                      const value = user[column.key]
                      const rendered = column.render
                        ? column.render(value, user)
                        : value

                      return (
                        <td
                          key={`${user.id}-${column.key}`}
                          className="px-6 py-4 text-slate-200 font-mono text-sm"
                          style={column.width ? { width: column.width } : undefined}
                        >
                          {rendered !== null && rendered !== undefined ? rendered : '—'}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pageCount > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-xs text-slate-400 font-mono">
            {pagination.total} {userType === 'organizer' ? 'organisateur(s)' : 'chanteur(s)'}
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => onPaginationChange?.(Math.max(1, pagination.page - 1), pagination.pageSize)}
              disabled={pagination.page === 1}
              className="px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-300 text-xs font-mono hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              Précédent
            </button>

            {paginationRange.map((page) => (
              <button
                key={page}
                onClick={() => onPaginationChange?.(page, pagination.pageSize)}
                className={`px-3 py-2 rounded text-xs font-mono transition ${
                  page === pagination.page
                    ? 'bg-cyan-600 text-white'
                    : 'bg-slate-800 border border-slate-600 text-slate-300 hover:bg-slate-700'
                }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => onPaginationChange?.(Math.min(pageCount, pagination.page + 1), pagination.pageSize)}
              disabled={pagination.page === pageCount}
              className="px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-300 text-xs font-mono hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              Suivant
            </button>
          </div>

          <select
            value={pagination.pageSize}
            onChange={(e) =>
              onPaginationChange?.(1, parseInt(e.target.value))
            }
            className="px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-300 text-xs font-mono focus:outline-none focus:border-cyan-500 transition"
          >
            <option value="10">10 / page</option>
            <option value="25">25 / page</option>
            <option value="50">50 / page</option>
            <option value="100">100 / page</option>
          </select>
        </div>
      )}
    </div>
  )
}

